import { createSlice, createEntityAdapter, createSelector } from "@reduxjs/toolkit";

const conversationAdapter = createEntityAdapter({
  selectId: (conversation) => conversation._id,
  sortComparer: (a, b) => b.updatedAt?.localeCompare(a.updatedAt),
});

const messageAdapter = createEntityAdapter({
  selectId: (message) => message._id,
  sortComparer: (a, b) => b.createdAt?.localeCompare(a.createdAt),
});

export const conversationSelectors = conversationAdapter.getSelectors(
  (state) => state.messenger.conversations
);

export const messageSelectors = messageAdapter.getSelectors(
  (state) => state.messenger.messages
);

// Memoized selector for unread count - prevents unnecessary re-renders
export const selectUnreadCount = createSelector(
  [(state) => state.messenger.conversations.ids, (state) => state.messenger.conversations.entities],
  (ids, entities) => {
    return ids.filter((id) => entities[id]?.unseenMessages > 0).length;
  }
);

// Selector for pending temp IDs (optimistic messages waiting for confirmation)
export const selectPendingTempIds = (state) => state.messenger.pendingTempIds;

const messengerSlice = createSlice({
  name: "messenger",
  initialState: {
    firstGetList: false,
    isGettingList: false,
    isGettingMessageList: false,
    isSendingMessage: false,
    isCreatingConversation: false,
    isGettingConversationDetail: false,
    conversations: conversationAdapter.getInitialState(),
    messages: messageAdapter.getInitialState({
      lastMessageTime: null,
    }),
    socket: null,
    isOpenModalTag: false,
    conversationId: "",
    conversationName: "",
    chosenConversation: {},
    isSending: false,
    isOpenModalSetTag: false,
    fonehouseTags: [],
    fonehouseTagsSelect: [],
    fonehouseTagsFilter: [],
    // Add new state for unread filter
    conversationFilter: "all", // "all" or "unread"
    unreadConversationIds: [],
    // Track pending temp message IDs (optimistic messages waiting for server confirmation)
    pendingTempIds: [],
    // Conversation stats for badge counts
    conversationStats: {
      totalConversations: 0,
      unreadCount: 0,
      seenButNotRepliedCount: 0,
    },
    // Last sent message data for socket emit
    lastSentMessage: null, // { conversationId, messageId }
    // Product preview state (for @productId tooltip in message input)
    productPreview: {
      isLoading: false,
      data: null,
      error: null,
      productId: null,
    },
  },
  reducers: {
    sendingMessage(state) {
      return { ...state, isSending: true };
    },
    finishSendMessage(state) {
      return { ...state, isSending: false };
    },
    firstGetListMessage(state) {
      return { ...state, firstGetList: true };
    },
    firstGetListMessageFalse(state) {
      return { ...state, firstGetList: false };
    },
    getConversationListRequest(state) {
      return { ...state, isGettingList: true };
    },
    getConversationListSuccess(state, action) {
      const { list, page, hasNextPage } = action.payload;
      state.isGettingList = false;
      if (page === 1) {
        conversationAdapter.setAll(state.conversations, list);
        state.conversations.page = 1;
      } else {
        // Only increment page if we received data
        if (list.length > 0) {
          state.conversations.page += 1;
          conversationAdapter.addMany(state.conversations, list);
        }
      }
      // Use hasNextPage from API response (fallback to length check if not provided)
      state.conversations.hasMore = hasNextPage ?? (list.length > 0 && list.length >= 30);
    },
    getMessageListRequest(state) {
      return { ...state, isGettingMessageList: true };
    },
    getMessageListSuccess(state, action) {
      const { list, time, conversationId } = action.payload;
      state.isGettingList = false;
      state.isGettingMessageList = false;
      if (!time) {
        messageAdapter.setAll(state.messages, list);
        state.messages.page = 1;
        state.firstGetList = true;
      } else {
        state.messages.page += 1;
        messageAdapter.addMany(state.messages, list);
      }
      const lastMessage = list[list.length - 1];
      if (lastMessage) {
        state.messages.lastMessageTime = new Date(
          list[list.length - 1].createdAt
        ).getTime();
      }

      state.messages.hasMore = list.length >= 15;
      const conversationEntity = conversationAdapter
        .getSelectors()
        .selectById(state.conversations, conversationId);
      conversationAdapter.setOne(state.conversations, {
        ...conversationEntity,
        unseenMessages: 0,
      });
    },
    stopLoading(state) {
      return { ...state, isGettingList: false };
    },
    sendMessageRequest: {
      reducer: (state) => {
        state.isSendingMessage = true;
        return state;
      },
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    sendMessageSuccess: {
      reducer: (state, action) => {
        state.isSendingMessage = false;
        state.lastSentMessage = action.payload ? {
          conversationId: action.payload.conversationId,
          messageId: action.payload._id,
        } : null;
        return state;
      },
      prepare: (payload, meta) => ({ payload, meta }),
    },
    updateMessage(state, action) {
      const { message } = action.payload;
      messageAdapter.upsertOne(state.messages, message);
    },
    // Increment unseen messages for immediate badge update
    incrementUnseenMessages(state, action) {
      const { conversationId } = action.payload;
      const conversationEntity = conversationAdapter
        .getSelectors()
        .selectById(state.conversations, conversationId);
      if (conversationEntity) {
        conversationAdapter.setOne(state.conversations, {
          ...conversationEntity,
          unseenMessages: (conversationEntity.unseenMessages || 0) + 1,
        });
      }
    },
    createConversationRequest: {
      reducer: (state) => {
        state.isCreatingConversation = true;
        return state;
      },
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    createConversationSuccess: {
      reducer: (state) => {
        state.isCreatingConversation = false;
        return state;
      },
      prepare: (payload, meta) => ({ payload, meta }),
    },
    getConversationDetailRequest(state) {
      return { ...state, isGettingConversationDetail: true };
    },
    getConversationDetailSuccess(state, action) {
      const { detail } = action.payload;
      conversationAdapter.upsertOne(state.conversations, detail);
    },
    seenConversationRequest(state) {
      return { ...state };
    },
    seenConversationSuccess(state, action) {
      const { conversationId } = action.payload;
      const conversationEntity = conversationAdapter
        .getSelectors()
        .selectById(state.conversations, conversationId);
      if (conversationEntity) {
        conversationAdapter.setOne(state.conversations, {
          ...conversationEntity,
          unseenMessages: 0,
        });
      }
    },
    triggerModalTag(state, action) {
      const conversationName = action.payload?.conversationEntities?.[
        action.payload?.conversationId
      ]?.users?.find((user) => user?.role === "user")?.name;
      return {
        ...state,
        isOpenModalTag: action.payload.visible,
        conversationId: action.payload?.conversationId,
        conversationName,
        chosenConversation:
          action.payload?.conversationEntities?.[
            action.payload?.conversationId
          ],
      };
    },
    createTagRequest(state) {
      return {
        ...state,
        isGettingList: true,
      };
    },
    triggerModalSetTag(state, action) {
      const userName = action.payload?.data?.users?.find(
        (user) => user?.role === "user"
      )?.name;
      return {
        ...state,
        isOpenModalSetTag: action.payload.visible,
        conversationName: userName,
        conversationId: action.payload?.data?._id,
      };
    },
    getFonehouseTagsRequest(state) {
      return {
        ...state,
      };
    },
    getFonehouseTagsSuccess(state, action) {
      const fonehouseTagsSelect = [];
      const fonehouseTagsFilter = [];
      action.payload?.tags?.forEach((tag, index) => {
        fonehouseTagsSelect.push({
          label: tag.name,
          value: `${tag.name}-${tag.color}`,
        });
        fonehouseTagsFilter.push({
          label: tag.name,
          value: `${tag.name}-${index}`,
          color: tag.color
        });
      });
      fonehouseTagsFilter.unshift({
        label: "Tất cả",
        value: "",
      });
      return {
        ...state,
        fonehouseTags: action.payload?.tags ?? [],
        fonehouseTagsSelect,
        fonehouseTagsFilter,
      };
    },
    removeTagRequest: {
      reducer: (state) => ({
        ...state,
        isRemovingTag: true, // Using a specific flag for tag removal
      }),
      prepare: (payload) => ({ payload }),
    },
    removeTagSuccess: (state) => ({
      ...state,
      isRemovingTag: false,
    }),
    removeTagFailure: (state) => ({
      ...state,
      isRemovingTag: false,
    }),
    // Add new reducers for unread filter
    setConversationFilter(state, action) {
      state.conversationFilter = action.payload;
      // Reset conversations when filter changes
      conversationAdapter.removeAll(state.conversations);
      state.conversations.page = 1;
      state.conversations.hasMore = true;
    },
    setUnreadConversationIds(state, action) {
      state.unreadConversationIds = action.payload;
    },
    getUnreadConversationsRequest(state) {
      return { ...state, isGettingList: true };
    },
    getSeenButNotRepliedRequest(state) {
      return { ...state, isGettingList: true };
    },
    // Conversation stats actions
    getConversationStatsRequest(state) {
      return state;
    },
    getConversationStatsSuccess(state, action) {
      state.conversationStats = action.payload;
    },
    // NEW Optimistic UI reducers - using single entity adapter
    // Add temp message directly to messages entity
    addTempMessage(state, action) {
      const { message } = action.payload;
      // message should have _id = tempId, isOptimistic = true
      messageAdapter.addOne(state.messages, message);
      // Track this tempId for later confirmation
      state.pendingTempIds.push(message._id);
    },
    // Confirm temp message: remove temp, add server message
    confirmTempMessage(state, action) {
      const { tempId, serverMessage } = action.payload;

      console.log('[Reducer] confirmTempMessage called:', { tempId, serverMessage });
      console.log('[Reducer] Current pendingTempIds:', state.pendingTempIds);
      console.log('[Reducer] Message exists?', !!state.messages.entities[tempId]);

      if (!tempId) {
        console.warn('[Reducer] confirmTempMessage: tempId is undefined!');
        return;
      }

      if (!serverMessage || !serverMessage._id) {
        console.warn('[Reducer] confirmTempMessage: serverMessage is invalid!', serverMessage);
        return;
      }

      // Remove temp message from entity
      messageAdapter.removeOne(state.messages, tempId);

      // Add server message to entity (with real _id)
      messageAdapter.upsertOne(state.messages, {
        ...serverMessage,
        isOptimistic: false,
      });

      // Remove from pending list
      state.pendingTempIds = state.pendingTempIds.filter(id => id !== tempId);

      console.log('[Reducer] confirmTempMessage done. Remaining pending:', state.pendingTempIds);
    },
    // Mark temp message as failed
    markTempMessageFailed(state, action) {
      const { tempId, error } = action.payload;
      const existingMessage = state.messages.entities[tempId];
      if (existingMessage) {
        messageAdapter.updateOne(state.messages, {
          id: tempId,
          changes: {
            isOptimistic: false,
            status: 'failed',
            error,
          },
        });
      }
      // Remove from pending list
      state.pendingTempIds = state.pendingTempIds.filter(id => id !== tempId);
    },
    // Remove failed temp message
    removeTempMessage(state, action) {
      const { tempId } = action.payload;
      messageAdapter.removeOne(state.messages, tempId);
      state.pendingTempIds = state.pendingTempIds.filter(id => id !== tempId);
    },
    // Retry failed message - mark as optimistic again
    retryTempMessage(state, action) {
      const { tempId } = action.payload;
      const existingMessage = state.messages.entities[tempId];
      if (existingMessage) {
        messageAdapter.updateOne(state.messages, {
          id: tempId,
          changes: {
            isOptimistic: true,
            status: undefined,
            error: undefined,
          },
        });
        // Re-add to pending list if not already there
        if (!state.pendingTempIds.includes(tempId)) {
          state.pendingTempIds.push(tempId);
        }
      }
    },
    // Handle incoming message from socket - auto detect and confirm temp message
    handleIncomingMessage(state, action) {
      const { serverMessage } = action.payload;
      const tempId = serverMessage.tempId;

      // Case 1: Backend returned tempId → confirm directly
      if (tempId && state.pendingTempIds.includes(tempId)) {
        messageAdapter.removeOne(state.messages, tempId);
        messageAdapter.upsertOne(state.messages, { ...serverMessage, isOptimistic: false });
        state.pendingTempIds = state.pendingTempIds.filter(id => id !== tempId);
        return;
      }

      // Case 2: No tempId but admin message → find matching temp message by content
      if (serverMessage.isAdmin === 1) {
        const matchingTempId = state.pendingTempIds.find(id => {
          const temp = state.messages.entities[id];
          if (!temp || temp.conversationId !== serverMessage.conversationId) {
            return false;
          }
          // For product messages, match by productId
          if (temp.type === 'product' && serverMessage.type === 'product') {
            return temp.productId === serverMessage.productId;
          }
          // For text messages, match by text content
          return temp.text === serverMessage.text;
        });

        if (matchingTempId) {
          // Found matching temp message → confirm
          messageAdapter.removeOne(state.messages, matchingTempId);
          messageAdapter.upsertOne(state.messages, { ...serverMessage, isOptimistic: false });
          state.pendingTempIds = state.pendingTempIds.filter(id => id !== matchingTempId);
          return;
        }
      }

      // Case 3: No match → new message from other source (user or other admin)
      messageAdapter.upsertOne(state.messages, serverMessage);
    },
    // Mark message as recalled
    markMessageAsRecalled(state, action) {
      const { messageId } = action.payload;
      const existingMessage = state.messages.entities[messageId];
      if (existingMessage) {
        messageAdapter.updateOne(state.messages, {
          id: messageId,
          changes: {
            isRecalled: true,
            text: null,
            attachments: [],
          },
        });
      }
    },
    // Update message text (for edited messages)
    updateMessageText(state, action) {
      const { messageId, text, isEdited, editedAt } = action.payload;
      const existingMessage = state.messages.entities[messageId];
      if (existingMessage) {
        messageAdapter.updateOne(state.messages, {
          id: messageId,
          changes: {
            text,
            isEdited,
            editedAt,
          },
        });
      }
    },
    // Update user online/offline status
    updateUserStatus(state, action) {
      const { userId, online, lastSeen } = action.payload;
      const { ids, entities } = state.conversations;

      ids.forEach((id) => {
        const conv = entities[id];
        if (conv?.users) {
          const updatedUsers = conv.users.map((user) =>
            user.id === userId ? { ...user, online, lastSeen } : user
          );
          conversationAdapter.updateOne(state.conversations, {
            id,
            changes: { users: updatedUsers },
          });
        }
      });
    },
    // Product preview actions (for @productId tooltip in message input)
    fetchProductPreviewRequest(state, action) {
      state.productPreview = {
        isLoading: true,
        data: null,
        error: null,
        productId: action.payload.productId,
      };
    },
    fetchProductPreviewSuccess(state, action) {
      state.productPreview = {
        isLoading: false,
        data: action.payload,
        error: null,
        productId: state.productPreview.productId,
      };
    },
    fetchProductPreviewFailure(state, action) {
      state.productPreview = {
        isLoading: false,
        data: null,
        error: action.payload.error || "Không tìm thấy sản phẩm",
        productId: state.productPreview.productId,
      };
    },
    clearProductPreview(state) {
      state.productPreview = {
        isLoading: false,
        data: null,
        error: null,
        productId: null,
      };
    },
  },
});

export const {
  firstGetListMessageFalse,
  firstGetListMessage,
  getConversationListRequest,
  getConversationListSuccess,
  getMessageListRequest,
  getMessageListSuccess,
  stopLoading,
  sendMessageRequest,
  sendMessageSuccess,
  updateMessage,
  incrementUnseenMessages,
  createConversationRequest,
  createConversationSuccess,
  getConversationDetailRequest,
  getConversationDetailSuccess,
  seenConversationRequest,
  seenConversationSuccess,
  triggerModalTag,
  createTagRequest,
  triggerModalSetTag,
  getFonehouseTagsRequest,
  getFonehouseTagsSuccess,
  sendingMessage,
  finishSendMessage,
  removeTagRequest,
  removeTagSuccess,
  removeTagFailure,
  setConversationFilter,
  setUnreadConversationIds,
  getUnreadConversationsRequest,
  getSeenButNotRepliedRequest,
  getConversationStatsRequest,
  getConversationStatsSuccess,
  // Optimistic UI actions (using single entity adapter)
  addTempMessage,
  confirmTempMessage,
  markTempMessageFailed,
  removeTempMessage,
  retryTempMessage,
  handleIncomingMessage,
  // Recall message
  markMessageAsRecalled,
  // Update message text
  updateMessageText,
  // Update user online/offline status
  updateUserStatus,
  // Product preview actions
  fetchProductPreviewRequest,
  fetchProductPreviewSuccess,
  fetchProductPreviewFailure,
  clearProductPreview,
} = messengerSlice.actions;

export default messengerSlice.reducer;
